'use client';

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { motion, type Transition } from 'motion/react';

import { cn } from '@/lib/utils';

/** The platform's highlight spring, so the two apps share one motion vocabulary. */
const SPRING: Transition = { type: 'spring', stiffness: 350, damping: 35 };

type Box = { x: number; y: number; width: number; height: number };

const HighlightContext = createContext<((box: Box | null) => void) | null>(null);

/**
 * One background block that travels between the items under the pointer.
 *
 * The block is a single element owned by the container, moved to each item's box — the platform's
 * `mode="parent"`. Mounting a block inside each item instead makes every move an unmount and a
 * mount: the first hover pops in at full opacity with no travel, and each move flashes as one
 * element is torn down and another built. A persistent element just slides.
 */
export function HoverHighlight({
  children,
  className,
  role,
}: PropsWithChildren<{ className?: string; role?: string }>) {
  const [box, setBox] = useState<Box | null>(null);

  return (
    <HighlightContext.Provider value={setBox}>
      {/* onBlur as well as onMouseLeave: the highlight follows focus too, so leaving by keyboard
          otherwise left it painted on the row focus had gone from. */}
      <div
        role={role}
        className={cn('relative', className)}
        onMouseLeave={() => setBox(null)}
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setBox(null);
        }}
      >
        <motion.span
          aria-hidden="true"
          // Never unmounted, so there is nothing to tear down mid-move. It fades rather than
          // appearing, and `initial={false}` keeps it from flying in from the origin on mount.
          initial={false}
          animate={
            box
              ? { opacity: 1, x: box.x, y: box.y, width: box.width, height: box.height }
              : { opacity: 0 }
          }
          transition={{ ...SPRING, opacity: { duration: 0.15 } }}
          // bg-accent, not bg-muted: in dark `--muted` and `--card` are both #252320, so on the
          // Industries panel the block was invisible.
          className="pointer-events-none absolute top-0 left-0 rounded-lg bg-accent"
        />
        {children}
      </div>
    </HighlightContext.Provider>
  );
}

export function HighlightItem({ className, children }: PropsWithChildren<{ className?: string }>) {
  const track = useContext(HighlightContext);
  const item = useRef<HTMLDivElement>(null);

  const report = useCallback(() => {
    const element = item.current;
    if (!element || !track) return;
    track({
      x: element.offsetLeft,
      y: element.offsetTop,
      width: element.offsetWidth,
      height: element.offsetHeight,
    });
  }, [track]);

  return (
    <div
      ref={item}
      // relative so the item paints above the block, which is a sibling behind it rather than a
      // child of it — that ordering is what stopped the background covering the text.
      className={cn('relative', className)}
      onMouseEnter={report}
      // Keyboard focus moves the block too, so tabbing reads the same as hovering — but only for
      // :focus-visible. The mobile menu focuses its first item on open for screen readers, and on
      // a pointer-opened menu that painted a hover block nobody was hovering.
      onFocus={event => {
        if (event.target.matches(':focus-visible')) report();
      }}
    >
      {children}
    </div>
  );
}
